import React from 'react';
import { Typography } from '@mui/material';
import { formatPhoneNumber, urlify } from '../../../components/Utils';
import { colors } from '../../../colors';
import LinkUnopenedIcon from '@mui/icons-material/PhonelinkOff';
import LinkOpenedIcon from '@mui/icons-material/Phonelink';
import LockedIcon from '@mui/icons-material/LockOutlined';
import NotReceivedIcon from '@mui/icons-material/CallMissedOutgoing';
import CheckIcon from '@mui/icons-material/Check';
import TimerIcon from '@mui/icons-material/AccessTime';
import moment from 'moment';

interface Props {
  header: string;
  content: string;
}

const parseUtcToLocal = (value: string) => moment.utc(value, 'DD.MM.YYYY HH:mm:ss').local();

export const getDurationUntilExpires = (expires: moment.Moment, now: moment.Moment) => {
  const diff = expires.diff(now)/1000;
  var hours = Math.floor(diff/(60*60));
  var mins = Math.floor(diff/60) - (hours * 60);
  return `${hours <= 0 ? '' : (hours + (hours === 1 ? ' time og ' : ' timer og '))}${mins} ${mins === 1 ? 'minutt' : 'minutter'} igjen`;
}

const getFormattedTooltipContent = (header: string, content: string, props) => {
  var formattedContent: any = content;
  var color = colors.black;

  switch (header) {

    case 'Mottaker':
      if (content) { formattedContent = formatPhoneNumber(content); }
      break;

    case 'Melding':
      if (!content) {
        color = colors.darkred;
        formattedContent = 'Melding ukjent';
        break;
      }
      formattedContent = <span dangerouslySetInnerHTML={{__html: urlify(content)}}/>;
      break;
    case 'Sendt':
      const sentMoment = parseUtcToLocal(content);
      formattedContent = sentMoment.isValid() ? sentMoment.format('DD.MM.YYYY HH:mm:ss') : "Ugyldig tidspunkt";
      break;
    case 'Første pålogging':
    case 'Siste pålogging':
      const openedMoment = parseUtcToLocal(content);
      const opened = openedMoment.isValid();
      color = opened ? colors.green : colors.darkred;
      formattedContent = opened ?
        <React.Fragment>
          <LinkOpenedIcon sx={{
            verticalAlign: 'bottom',
            height: '0.9em',
            color: colors.green
          }} /> {openedMoment.format('DD.MM.YYYY HH:mm:ss')}
        </React.Fragment> :
        <React.Fragment>
          <LinkUnopenedIcon sx={{
            verticalAlign: 'bottom',
            height: '0.9em',
            color: colors.darkred
          }} /> Ingen pålogging
        </React.Fragment>;
      break;

    case 'Bekreftet mottatt':
      const receivedMoment = parseUtcToLocal(content);
      const received = receivedMoment.isValid();
      color = received ? colors.green : colors.darkred;
      formattedContent = received ?
        <React.Fragment>
          <CheckIcon sx={{
            verticalAlign: 'bottom',
            height: '0.9em',
            color: colors.green
          }} />
          {receivedMoment.format('DD.MM.YYYY HH:mm:ss')}
        </React.Fragment> :
        <React.Fragment>
          <NotReceivedIcon sx={{
            verticalAlign: 'bottom',
            height: '0.9em',
            color: colors.darkred
          }} />
          Ikke mottatt
        </React.Fragment>;
      break;

    case 'Utløpsdato':
      const expires = parseUtcToLocal(content);
      if (!expires.isValid()) { break; }
      const now = moment().local();
      const hasExpired = expires < now;
      color = hasExpired ? colors.darkred : colors.green;
      formattedContent = hasExpired ?
        <React.Fragment>
          <LockedIcon sx={{
            verticalAlign: 'bottom',
            height: '0.9em',
            color: colors.darkred
          }} />
          {expires.format('DD.MM.YYYY HH:mm:ss')}
        </React.Fragment> :
        <React.Fragment>
          <TimerIcon sx={{
            verticalAlign: 'bottom',
            height: '0.9em',
            color: colors.green
          }} />
          {getDurationUntilExpires(expires, now)} ({expires.format('DD.MM.YYYY HH:mm:ss')})
        </React.Fragment>;
      break;

    default:
      break;
  }

  return <span style={{ color : color}}>{formattedContent}</span>;
};

export default function TooltipContent(props: Props) {
  return (
    <Typography sx={{ whiteSpace: 'pre-line' }}>
      <b>{props.header}: </b>{getFormattedTooltipContent(props.header, props.content, props)}
    </Typography>
  )
};
